import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  ArrowDownRight,
  ArrowUpRight,
  Globe2,
  Sparkles,
  Trophy,
  Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { PackageCard } from '@/components/initiative/PackageCard';
import { SponsorCalculator } from '@/components/initiative/SponsorCalculator';
import { SponsorForm } from '@/components/initiative/SponsorForm';
import { PalestinianDiasporaAnalytics } from '@/components/initiative/PalestinianDiasporaAnalytics';
import { SportsImpactAnalytics } from '@/components/initiative/SportsImpactAnalytics';
import { SponsorAudiences } from '@/components/initiative/SponsorAudiences';
import { Counter } from '@/components/initiative/Counter';
import {
  internationalTargets,
  sponsorFaq,
  sponsorPackages,
  type SponsorBreakdown,
} from '@/data/mock/initiative';

export default function InitiativeSponsor() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';
  const ArrowIcon = isRTL ? ArrowDownRight : ArrowUpRight;

  const [customConfig, setCustomConfig] = useState<SponsorBreakdown | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const backdropScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const backdropY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const scrollProgress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  const handleApplyCustom = (mix: SponsorBreakdown) => {
    setCustomConfig(mix);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Marquee items: package teasers — counts only, no prices.
  const marquee = sponsorPackages.flatMap((p) => [
    `${p.name[lang]} · ${p.totalGrants} ${t('initiative.scholarships.grants')}`,
  ]);
  const marqueeLoop = [...marquee, ...marquee, ...marquee];

  return (
    <div className="initiative-theme font-body-init overflow-x-hidden bg-background">
      {/* ───────── HERO (magazine-cover, compact) ───────── */}
      <section
        ref={heroRef}
        className="relative isolate pt-8 pb-16 lg:pb-20 overflow-hidden"
      >
        <motion.img
          aria-hidden
          src="/img/initiative/hero-sponsor.webp"
          alt=""
          style={{ scale: backdropScale, y: backdropY }}
          className="absolute inset-0 h-full w-full object-cover opacity-50 pointer-events-none"
          loading="eager"
        />
        <motion.div
          aria-hidden
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background/90 pointer-events-none"
        />
        <div aria-hidden className="absolute inset-0 bg-line-grid opacity-20 pointer-events-none" />

        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.18, 1], opacity: [0.2, 0.45, 0.2], x: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -left-32 w-[44rem] h-[44rem] bg-accent/12 rounded-full blur-3xl pointer-events-none"
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.12, 0.3, 0.12], x: [0, -40, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
          className="absolute -bottom-24 -right-32 w-[40rem] h-[40rem] bg-primary-foreground/10 rounded-full blur-3xl pointer-events-none"
        />

        {/* Publication ribbon */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm">
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-foreground/85 font-bold">
                ★ {t('initiative.name')} — {t('initiative.slogan')}
              </span>
              <div className="flex items-center gap-4">
                <Link
                  to="/initiative"
                  className="font-mono-mark text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-accent transition-colors inline-flex items-center gap-1.5"
                >
                  {isRTL ? 'العودة للمبادرة' : 'Back to initiative'}
                  <ArrowIcon className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          style={{ y: contentY }}
          className="container mx-auto px-4 relative z-10 pt-10 lg:pt-14"
        >
          {/* Compact launch badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            className="flex justify-center mb-6"
          >
            <motion.div
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-accent/12 border border-accent/40 backdrop-blur-sm"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent" />
              </span>
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-accent font-bold">
                № 02 · {isRTL ? 'الرعاية' : 'Sponsorship'}
              </span>
              <span className="hidden sm:inline-block h-2.5 w-px bg-accent/40" />
              <span className="hidden sm:inline font-editorial-italic text-[10px] text-accent/80">
                {t('initiative.launchLabel')} · {t('initiative.launchDateShort')}
              </span>
            </motion.div>
          </motion.div>

          {/* Headline — 2 lines */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={cn(
              'font-display font-black uppercase text-balance text-center max-w-5xl mx-auto',
              'text-4xl sm:text-5xl lg:text-6xl xl:text-7xl',
              isRTL
                ? 'font-display-ar normal-case leading-[1.4] py-1'
                : 'leading-[0.95]'
            )}
          >
            {(() => {
              const lines = isRTL
                ? [
                    { text: 'ارعَ حزمة.', accent: false },
                    { text: 'موّل عودة.', accent: true },
                  ]
                : [
                    { text: 'Sponsor a tier.', accent: false },
                    { text: 'Fund a comeback.', accent: true },
                  ];
              return lines.map((line, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{
                    duration: 0.7,
                    delay: 0.3 + i * 0.12,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                  className={cn(
                    'block',
                    isRTL && 'pb-1',
                    line.accent &&
                      'bg-gradient-to-r from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                  )}
                >
                  {line.text}
                </motion.span>
              ));
            })()}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-5 font-editorial-italic text-base sm:text-lg text-foreground/75 leading-relaxed text-center max-w-2xl mx-auto"
          >
            "{t('initiative.sponsor.heroSubtitle')}"
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-7 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="#packages"
              className="group inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent text-accent-foreground font-bold uppercase tracking-[0.18em] text-xs btn-glow transition-transform hover:-translate-y-0.5"
            >
              <Trophy className="h-4 w-4" />
              {isRTL ? 'الحزم الجاهزة' : 'See packages'}
              <ArrowIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#custom-package"
              className="group inline-flex items-center gap-2 h-11 px-5 rounded-md border-2 border-foreground/20 hover:border-accent text-xs font-bold uppercase tracking-[0.18em] text-foreground/85 hover:text-accent transition-colors"
            >
              <Zap className="h-4 w-4 text-accent" />
              {isRTL ? 'صمّم حزمتك' : 'Build your own'}
            </a>
          </motion.div>
        </motion.div>

        {/* Bottom stats data strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="relative z-10 mt-10 lg:mt-14"
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm divide-x divide-border/40 rtl:divide-x-reverse">
              {[
                { num: 150, label: isRTL ? 'منحة مجانية' : 'Free scholarships', highlight: true },
                { num: 4, label: isRTL ? 'منصات شريكة' : 'Partner platforms' },
                { num: 25, label: isRTL ? 'كورس مختار' : 'Curated courses' },
                { date: '6·6', label: isRTL ? 'يوم الانطلاق' : 'Launch day' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 1.05 + i * 0.06 }}
                  whileHover={{ y: -2 }}
                  className="group relative px-4 py-4 text-center"
                >
                  <div
                    className={cn(
                      'font-display font-black text-3xl lg:text-4xl leading-none num-tabular transition-colors',
                      s.highlight
                        ? 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                        : 'text-foreground group-hover:text-accent'
                    )}
                  >
                    {s.date ? s.date : <Counter value={s.num} />}
                  </div>
                  <div className="mt-2 font-mono-mark text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          aria-hidden
          style={{ width: scrollProgress }}
          className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-accent via-accent/80 to-transparent z-20"
        />
      </section>

      {/* ───────── MARQUEE ───────── */}
      <section className="relative py-5 bg-surface-2 text-foreground overflow-hidden border-y border-border/40">
        <div className="init-marquee">
          {marqueeLoop.map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-6 px-6 font-display font-black text-xl sm:text-2xl uppercase whitespace-nowrap"
            >
              {item}
              <span className="text-accent/70 text-lg">★</span>
            </span>
          ))}
        </div>
      </section>

      {/* ───────── FOOTBALL ECOSYSTEM (scale stats) ───────── */}
      <SportsImpactAnalytics />

      {/* ───────── DIASPORA / HOST COUNTRIES ───────── */}
      <PalestinianDiasporaAnalytics />

      {/* ───────── AUDIENCES + CSR + LOGO WALL ───────── */}
      <SponsorAudiences />

      {/* ───────── PACKAGES ───────── */}
      <section id="packages" className="relative py-24 lg:py-32 scroll-mt-20">
        <div className="absolute inset-0 -z-10 bg-dot-grid-light opacity-30 pointer-events-none" />
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-6 mb-14 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-8"
            >
              <div className="section-mark">
                № 02 — {isRTL ? 'الحزم' : 'Packages'}
              </div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl text-balance max-w-3xl',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {t('initiative.sponsor.packagesTitle')}
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="lg:col-span-4 font-editorial-italic text-base lg:text-lg text-foreground/70 leading-relaxed"
            >
              "{t('initiative.sponsor.packagesSubtitle')}"
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-7 max-w-6xl mx-auto items-stretch">
            {sponsorPackages.map((pkg, idx) => (
              <PackageCard key={pkg.id} pkg={pkg} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ───────── CUSTOM CALCULATOR ───────── */}
      <section
        id="custom-package"
        className="relative py-24 lg:py-32 bg-primary text-primary-foreground overflow-hidden scroll-mt-20"
      >
        <div className="absolute inset-0 bg-line-grid opacity-20" />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 right-[-10rem] w-[34rem] h-[34rem] bg-accent/15 rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-12 gap-6 mb-12 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-8"
            >
              <div className="section-mark text-accent">
                № 03 — {isRTL ? 'حزمة مخصصة' : 'Build your own'}
              </div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl text-balance max-w-3xl',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {isRTL ? (
                  <>
                    اختر تركيبتك.{' '}
                    <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                      السعر يتحدّث مباشرة.
                    </span>
                  </>
                ) : (
                  <>
                    Pick your mix.{' '}
                    <span className="bg-gradient-to-r from-accent to-accent/60 bg-clip-text text-transparent">
                      Live pricing.
                    </span>
                  </>
                )}
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="lg:col-span-4 font-editorial-italic text-base lg:text-lg text-primary-foreground/70 leading-relaxed"
            >
              "{isRTL
                ? 'صمّم حزمة خاصة بمؤسستك. كل منحة تغطي مدرباً واحداً يدرس كورساً واحداً.'
                : 'Build a configuration for your organisation. Each scholarship covers one coach studying one course.'}"
            </motion.p>
          </div>

          <div className="max-w-6xl mx-auto">
            <SponsorCalculator onApply={handleApplyCustom} />
          </div>
        </div>
      </section>

      {/* ───────── INTERNATIONAL PARTNERS ───────── */}
      <section className="relative py-24 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-6 mb-14 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-8"
            >
              <div className="section-mark">
                № 04 — {isRTL ? 'الشراكات الدولية' : 'International'}
              </div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl text-balance max-w-3xl',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {t('initiative.sponsor.partnersTitle')}
              </h2>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {internationalTargets.map((target, idx) => (
              <motion.div
                key={target.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: Math.min(idx * 0.08, 0.4),
                }}
                whileHover={{ y: -4 }}
                className="relative group p-6 lg:p-7 rounded-2xl bg-card border border-border/60 backdrop-blur-xl overflow-hidden lift-on-hover"
              >
                <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-accent/0 group-hover:bg-accent/10 blur-2xl transition-colors duration-500" />
                <div className="relative flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                    <Globe2 className="h-5 w-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                      / 0{idx + 1}
                    </div>
                    <h3 className="font-display font-black text-xl lg:text-2xl uppercase leading-tight text-foreground">
                      {target.name[lang]}
                    </h3>
                    <div className="text-xs text-muted-foreground mt-1 font-mono-mark uppercase tracking-wider">
                      {target.domain[lang]}
                    </div>
                    <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                      {target.entryPoint[lang]}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30">
                      <Sparkles className="h-3 w-3 text-accent" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-accent">
                        {target.status[lang]}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── INQUIRY FORM ───────── */}
      <section
        ref={formRef}
        className="relative py-24 lg:py-32 bg-surface-1/40 scroll-mt-20"
      >
        <div className="absolute inset-0 bg-dot-grid-light opacity-30 pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <div className="section-mark mx-auto inline-block">
                № 05 — {isRTL ? 'نموذج الطلب' : 'Inquiry'}
              </div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {t('initiative.sponsor.formTitle')}
              </h2>
              <p className="mt-5 font-editorial-italic text-base lg:text-lg text-foreground/70 leading-relaxed max-w-xl mx-auto">
                "{t('initiative.sponsor.formSubtitle')}"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="relative p-6 sm:p-8 lg:p-10 rounded-3xl bg-card/90 border-2 border-border/60 backdrop-blur-xl shadow-2xl shadow-primary/5"
            >
              <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-accent text-primary font-mono-mark text-[10px] font-bold uppercase tracking-widest shadow-lg">
                {isRTL ? 'رد خلال 48 ساعة' : '48-hour reply'}
              </div>
              <SponsorForm customConfig={customConfig} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="relative py-24 lg:py-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 max-w-2xl mx-auto"
          >
            <div className="section-mark mx-auto inline-block">
              № 06 — {isRTL ? 'الأسئلة' : 'FAQ'}
            </div>
            <h2
              className={cn(
                'mt-4 font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-balance',
                isRTL && 'font-display-ar normal-case leading-[1.1]'
              )}
            >
              {t('initiative.sponsor.faqTitle')}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {sponsorFaq.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: Math.min(idx * 0.07, 0.35),
                  }}
                >
                  <AccordionItem
                    value={`item-${idx}`}
                    className="rounded-xl border-2 border-border/60 bg-card/80 backdrop-blur-xl px-5 transition-colors hover:border-accent/40 data-[state=open]:border-accent/60"
                  >
                    <AccordionTrigger className="text-base font-bold text-foreground hover:no-underline group">
                      <div className="flex items-center gap-3 text-start">
                        <span className="font-mono-mark text-[10px] text-accent font-bold uppercase tracking-widest">
                          / 0{idx + 1}
                        </span>
                        <span className="group-hover:text-accent transition-colors">
                          {item.question[lang]}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-foreground/80 leading-relaxed pl-9">
                      {item.answer[lang]}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
